import { Button, Container, Grid, Input, Typography } from '@mui/material'
import { GridColumns, GridRenderCellParams, GridRowParams, GridActionsCellItem, DataGrid, GridValueGetterParams } from '@mui/x-data-grid'
import { DataGridStyles, Topbar, Avatar, Icon, ConfirmDialog, SnackBarContext, Modal, Page } from 'components'
import { inject, observer } from 'mobx-react'
import { User } from 'models'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AuthStore, RoutingStore } from '../stores'
import { MainSidebar, UserForm } from '../views'
import styled from 'styled-components'
import { UserType, Language } from 'types'
import { theme } from 'config'
import { DatagridPlaceholder } from 'components/datagridPlaceholder'
import { caseInsensitiveComparator } from 'util/util'

export interface ISystemUsersProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

/**
 * Allows admins to add more admins, bgi_manager and editors to the system
 *
 * @type Admin
 */
export const SystemUsers = inject('authStore', 'routingStore')(observer((props: ISystemUsersProps) => {
  const [users, setUsers]                         = React.useState<User[]>([]);
  const [user,  setUser]                          = React.useState<User>(null);
  const [confirmValue, setConfirmValue]           = React.useState<string>("");
  const [openModal,         setOpenModal]         = React.useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false)
  const { t, i18n } = useTranslation();
  const { toast }   = React.useContext(SnackBarContext);

  React.useEffect(() => {
    User.loadSystemUsers().then(u => setUsers(u));
  }, []);

  const columns: GridColumns = [
    {
      field:           'name',
      headerName:      t('name'),
      flex:            1,
      headerClassName: 'columnHeader',
      minWidth:        30,
      renderCell:      (params: GridRenderCellParams<string, User>) => (
        <Avatar user = {params.row} />
      ),
      sortable:        true,
      valueGetter:     (params: GridValueGetterParams) => params.row.getFullName,
      sortComparator:  caseInsensitiveComparator,
    },{
      flex:            1,
      field:           'user_type',
      headerName:      t('role'),
      headerClassName: 'columnHeader',
    },{
      field: 'action',
      type:  'actions',
      getActions: (params: GridRowParams<User>) => [
        <GridActionsCellItem 
          icon       = {<StyledIcon size='size-18' iconId="bin" />}
          showInMenu = {false}
          label      = {t('delete')}
          onClick    = {() => {setOpenConfirmDialog(true); console.log(params.row); setUser(params.row)}}
        />,
      ],
    },
  ];
  

  return (
    <Page sidebar={<MainSidebar />} topbar={<Topbar />}>
      <Container maxWidth='xl'>
        <Grid pt={3} container>
          <Grid
            item
            xs             = {12}
            mb             = {3}
            display        = 'flex'
            justifyContent = 'space-between'
            alignItems     = 'center'
          >
            <Typography variant='h2'>{t('bgi_roles')}</Typography>
            <Button
              variant   = 'contained'
              startIcon = {<Icon iconId='add' />}
              onClick   = {() => {
                setUser(User.create({
                    title:           '',
                    first_name:      '',
                    last_name:       '',
                    personal_number: '',
                    email:           '',
                    phone:           '',
                    user_type:       UserType.Editor,
                    language:        Language.SV,
                    password:        "pw"
                  })
                );
                setOpenModal(true);
              }}
            >
              {t('add_system_user')}
            </Button>
          </Grid>
        </Grid>
        <DataGridStyles>
          <DataGrid
            autoHeight
            disableColumnSelector
            disableSelectionOnClick
            rowHeight          = {66}
            density            = 'standard'
            columns            = {columns}
            pageSize           = {25}
            rows               = {users}
            components = {{
              NoRowsOverlay: () =>
              (<DatagridPlaceholder title={t('datagrid_placeholder_admin_user_role_list')}/>)
            }}
            rowsPerPageOptions = {[25]}
          />
        </DataGridStyles>
      </Container>
      <ConfirmDialog 
        content = {<Trans  t={t} values={{user: user?.email}} i18nKey='delete_user_confirm_details' components={{bold: <strong />}}/>}
        open              = {openConfirmDialog}
        disabled          = {confirmValue !== user?.email}
        confirmInput      = {<Input value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)} />}
        onClose           = {() => setOpenConfirmDialog(false)}
        title             = {t('confirm_delete_user_question') + '?'}
        confirmButtonText = {t('delete')}
        cancelButtonText  = {t('cancel')}
        onCancel          = {() => {setOpenConfirmDialog(false); setConfirmValue("")}}
        onConfirm         = {async () => {
          if (await user.remove()) {
            setUsers(users.filter((u) => user.id !== u.id))
          } else {
            toast(t('toast_error_remove_manager'), 'error')
          }
          setOpenConfirmDialog(false)
          setConfirmValue("")
        }}
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Typography mb={3} variant='h3'>
          {t('add_system_user')}
        </Typography>
          <Grid
            container
            mb            = {4}
            rowSpacing    = {1}
            columnSpacing = {theme.columnSpacing}
          >
            <UserForm
              required
              dontSave
              isSystemUser
              hasConfirmationButtons
              user = {user}
              onSave = {async () => {
                if(await user.save()) {
                  setUsers([...users, user])
                  setOpenModal(false)
                } else {
                  toast(t('toast_error_add_system_user_fail'), 'error')
                }
              }}
            
              onCancel = {() => setOpenModal(false)}
            />
          </Grid>
        </Modal>
    </Page>
  );
}));


const StyledIcon = styled(Icon)`
  svg {
    color: black;
  }
`